import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateFilterType } from 'src/pages/performance/tabs/overview-tab';
import {
  useGetMartAdvertisersQuery,
  useGetMartCampaignsQuery,
  useGetMartLineItemsQuery,
  useGetMartSourcesQuery,
} from 'src/redux/admin/mart-reporting/mart-reporting.endpoints';
import { MartFilterOption } from 'src/redux/admin/mart-reporting/mart-reporting.types';
import { formatPlatformLabel, toMartSource } from 'src/pages/reports/utils/format-reporting-labels';
import { BetaFilterSelectSkeleton } from './beta-charts/beta-chart-skeletons';
import { MartReportFilterSelect } from './mart-report-filter-select';
import { MartReportsDateRangePicker } from './mart-reports-date-range-picker';

export interface MartReportSelection {
  source: string;
  advertisers: MartFilterOption[];
  campaigns: MartFilterOption[];
  lineItems: MartFilterOption[];
  dateFilter: DateFilterType;
  startDate: string;
  endDate: string;
}

interface MartReportsFilterBarProps {
  onSelectionChange: (selection: MartReportSelection) => void;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return {
    startDate: formatDateLocal(start),
    endDate: formatDateLocal(end),
  };
};

const MartReportsFilterBar = ({ onSelectionChange }: MartReportsFilterBarProps) => {
  const [searchParams] = useSearchParams();
  const defaults = useMemo(() => getDefaultDateRange(), []);

  const deepLink = useMemo(() => {
    const source = searchParams.get('source')?.trim() || '';
    const campaign = searchParams.get('campaign')?.trim() || '';
    const campaignName = searchParams.get('campaignName')?.trim() || '';
    const startDate = searchParams.get('startDate')?.trim() || '';
    const endDate = searchParams.get('endDate')?.trim() || '';
    return {
      source,
      campaign,
      campaignName,
      startDate: ISO_DATE.test(startDate) ? startDate : null,
      endDate: ISO_DATE.test(endDate) ? endDate : null,
    };
  }, [searchParams]);

  const hasDeepLinkDates = Boolean(deepLink.startDate && deepLink.endDate);

  const [selectedSource, setSelectedSource] = useState(() =>
    deepLink.source ? toMartSource(deepLink.source) : '',
  );
  const [selectedAdvertisers, setSelectedAdvertisers] = useState<MartFilterOption[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<MartFilterOption[]>([]);
  const [selectedLineItems, setSelectedLineItems] = useState<MartFilterOption[]>([]);
  const [dateFilter, setDateFilter] = useState<DateFilterType>(
    hasDeepLinkDates ? 'custom' : 'last_month',
  );
  const [startDate, setStartDate] = useState(
    hasDeepLinkDates && deepLink.startDate ? deepLink.startDate : defaults.startDate,
  );
  const [endDate, setEndDate] = useState(
    hasDeepLinkDates && deepLink.endDate ? deepLink.endDate : defaults.endDate,
  );

  const sourceDeepLinkApplied = useRef(!deepLink.source);
  const campaignDeepLinkApplied = useRef(!deepLink.campaign);

  const { data: sources = [], isLoading: sourcesLoading } = useGetMartSourcesQuery();
  const advertiserIds = selectedAdvertisers.map((advertiser) => advertiser.id);
  const campaignIds = selectedCampaigns.map((campaign) => campaign.id);

  const { data: advertisers = [], isFetching: advertisersFetching } = useGetMartAdvertisersQuery(
    { source: selectedSource },
    { skip: !selectedSource },
  );
  const {
    data: campaigns = [],
    isFetching: campaignsFetching,
    isUninitialized: campaignsUninitialized,
  } = useGetMartCampaignsQuery(
    { source: selectedSource, advertiser_ids: advertiserIds },
    { skip: !selectedSource },
  );
  const { data: lineItems = [], isFetching: lineItemsFetching } = useGetMartLineItemsQuery(
    {
      source: selectedSource,
      advertiser_ids: advertiserIds,
      campaign_ids: campaignIds,
    },
    { skip: !selectedSource },
  );

  useEffect(() => {
    if (sources.length === 0) return;

    if (!sourceDeepLinkApplied.current && deepLink.source) {
      const resolved = toMartSource(deepLink.source);
      const match =
        sources.find((source) => source.id === resolved) ??
        sources.find((source) => source.id === deepLink.source);
      sourceDeepLinkApplied.current = true;
      if (match) {
        setSelectedSource(match.id);
        return;
      }
    }

    setSelectedSource((current) => current || sources[0].id);
  }, [sources, deepLink.source]);

  useEffect(() => {
    if (campaignDeepLinkApplied.current || !deepLink.campaign || !selectedSource) return;
    if (campaignsUninitialized || campaignsFetching) return;

    const match = campaigns.find((campaign) => campaign.id === deepLink.campaign);
    if (match) {
      setSelectedCampaigns([match]);
    } else {
      setSelectedCampaigns([
        {
          id: deepLink.campaign,
          name: deepLink.campaignName || deepLink.campaign,
        },
      ]);
    }
    campaignDeepLinkApplied.current = true;
  }, [
    deepLink.campaign,
    deepLink.campaignName,
    selectedSource,
    campaigns,
    campaignsFetching,
    campaignsUninitialized,
  ]);

  useEffect(() => {
    onSelectionChange({
      source: selectedSource,
      advertisers: selectedAdvertisers,
      campaigns: selectedCampaigns,
      lineItems: selectedLineItems,
      dateFilter,
      startDate,
      endDate,
    });
  }, [
    selectedSource,
    selectedAdvertisers,
    selectedCampaigns,
    selectedLineItems,
    dateFilter,
    startDate,
    endDate,
    onSelectionChange,
  ]);

  const handleDateChange = (filter: DateFilterType, nextStartDate: string, nextEndDate: string) => {
    setDateFilter(filter);
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
  };

  const campaignOptions =
    selectedCampaigns.length > 0 &&
    !campaigns.some((campaign) => campaign.id === selectedCampaigns[0].id)
      ? [...selectedCampaigns, ...campaigns]
      : campaigns;

  return (
    <div className="mb-6 rounded-xl border border-[#E8E9ED] bg-white p-4 shadow-[0_1px_3px_rgba(13,15,26,0.04)]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Platform
            </label>
            {sourcesLoading ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
                aria-label="Platform"
                value={selectedSource}
                active={Boolean(selectedSource)}
                onChange={(value) => {
                  setSelectedSource(value);
                  setSelectedAdvertisers([]);
                  setSelectedCampaigns([]);
                  setSelectedLineItems([]);
                }}
              >
                {sources.map((source) => (
                  <option key={source.id} value={source.id}>
                    {formatPlatformLabel(source.id)}
                  </option>
                ))}
              </MartReportFilterSelect>
            )}
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Advertiser
            </label>
            {sourcesLoading || (selectedSource && advertisersFetching && advertisers.length === 0) ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
                aria-label="Advertiser"
                value={selectedAdvertisers[0]?.id ?? ''}
                active={selectedAdvertisers.length > 0}
                onChange={(value) => {
                  const advertiser = advertisers.find((item) => item.id === value);
                  setSelectedAdvertisers(advertiser ? [advertiser] : []);
                  setSelectedCampaigns([]);
                  setSelectedLineItems([]);
                }}
              >
                <option value="">All advertisers</option>
                {advertisers.map((advertiser) => (
                  <option key={advertiser.id} value={advertiser.id}>
                    {advertiser.name}
                  </option>
                ))}
              </MartReportFilterSelect>
            )}
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Campaign
            </label>
            {sourcesLoading || (selectedSource && campaignsFetching && campaigns.length === 0) ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
                aria-label="Campaign"
                value={selectedCampaigns[0]?.id ?? ''}
                active={selectedCampaigns.length > 0}
                onChange={(value) => {
                  const campaign = campaignOptions.find((item) => item.id === value);
                  setSelectedCampaigns(campaign ? [campaign] : []);
                  setSelectedLineItems([]);
                }}
              >
                <option value="">All campaigns</option>
                {campaignOptions.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </MartReportFilterSelect>
            )}
          </div>

          <div className="min-w-0">
            <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
              Line item
            </label>
            {sourcesLoading || (selectedSource && lineItemsFetching && lineItems.length === 0) ? (
              <BetaFilterSelectSkeleton />
            ) : (
              <MartReportFilterSelect
                aria-label="Line item"
                value={selectedLineItems[0]?.id ?? ''}
                active={selectedLineItems.length > 0}
                onChange={(value) => {
                  const lineItem = lineItems.find((item) => item.id === value);
                  setSelectedLineItems(lineItem ? [lineItem] : []);
                }}
              >
                <option value="">All line items</option>
                {lineItems.map((lineItem) => (
                  <option key={lineItem.id} value={lineItem.id}>
                    {lineItem.name}
                  </option>
                ))}
              </MartReportFilterSelect>
            )}
          </div>
        </div>

        <div className="shrink-0">
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.5px] text-[#8A8EA0]">
            Date range
          </label>
          <MartReportsDateRangePicker
            dateFilter={dateFilter}
            startDate={startDate}
            endDate={endDate}
            onChange={handleDateChange}
          />
        </div>
      </div>
    </div>
  );
};

export default MartReportsFilterBar;
